import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DataTreatmentModal } from '../ui/DataTreatmentModal';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

const membershipSchema = z.object({
  tipoDocumento: z.string().min(1, 'Tipo de documento es requerido'),
  numeroDocumento: z.string().min(1, 'Número de documento es requerido').min(6, 'El número debe tener al menos 6 dígitos'),
  nombres: z.string().min(1, 'Nombres son requeridos').min(2, 'Mínimo 2 caracteres'),
  apellidos: z.string().min(1, 'Apellidos son requeridos').min(2, 'Mínimo 2 caracteres'),
  genero: z.string().min(1, 'Género es requerido'),
  estadoCivil: z.string().min(1, 'Estado civil es requerido'),
  zonaBarrio: z.string().min(1, 'Zona o barrio es requerido').min(2, 'Mínimo 2 caracteres'),
  email: z.union([
    z.string().email('Email inválido'),
    z.string().length(0)
  ]).optional(),
  telefono: z.string().min(1, 'Teléfono es requerido').min(10, 'Teléfono debe tener al menos 10 dígitos'),
  fechaNacimiento: z.string().min(1, 'Fecha de nacimiento es requerida'),
  consentimientoDatos: z.boolean().refine(val => val === true, {
    message: 'Debe aceptar la política de tratamiento de datos personales'
  }),
  autorizacionImagen: z.boolean().refine(val => val === true, {
    message: 'Debe autorizar el uso de imagen para participar en actividades'
  })
});

type MembershipFormData = z.infer<typeof membershipSchema>;

interface MembershipFormProps {
  isOpen: boolean;
  onClose: () => void;
  eventTitle?: string;
}

export const MembershipForm: React.FC<MembershipFormProps> = ({ 
  isOpen, 
  onClose,
  eventTitle = "Inscripción como miembro"
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedMemberData, setSubmittedMemberData] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch
  } = useForm<MembershipFormData>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      consentimientoDatos: false,
      autorizacionImagen: false
    }
  });

  const consentimientoDatos = watch('consentimientoDatos');
  const autorizacionImagen = watch('autorizacionImagen');

  const onSubmit = async (data: MembershipFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      // Preparar datos para envío al backend
      const memberData = {
        tipoDocumento: data.tipoDocumento,
        numeroDocumento: data.numeroDocumento,
        nombres: data.nombres,
        apellidos: data.apellidos,
        genero: data.genero,
        estadoCivil: data.estadoCivil,
        zonaBarrio: data.zonaBarrio,
        fechaNacimiento: data.fechaNacimiento,
        email: data.email || undefined,
        telefono: data.telefono,
        consentimientoDatos: data.consentimientoDatos,
        autorizacionImagen: data.autorizacionImagen
      };

      // Enviar datos al backend
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.memberships.create}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(memberData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear la membresía');
      }

      const result = await response.json();
      
      // Guardar datos del miembro creado para mostrar en el modal de bienvenida
      setSubmittedMemberData({
        nombres: data.nombres,
        apellidos: data.apellidos,
        email: data.email || undefined,
        genero: data.genero,
        memberId: result.data?.id || Math.random().toString(36).substr(2, 9),
        nombreCompleto: `${data.nombres} ${data.apellidos}`,
        documentoCompleto: `${data.tipoDocumento} ${data.numeroDocumento}`,
        fechaRegistro: result.data?.creadoEn || new Date().toISOString()
      });
      
      // Mostrar modal de bienvenida
      setShowWelcomeModal(true);
      reset();
      
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const tiposDocumento = [
    { value: '', label: 'Seleccionar tipo' },
    { value: 'CC', label: 'Cédula de Ciudadanía' },
    { value: 'CE', label: 'Cédula de Extranjería' },
    { value: 'TI', label: 'Tarjeta de Identidad' },
    { value: 'PP', label: 'Pasaporte' },
    { value: 'RC', label: 'Registro Civil' }
  ];

  const generos = [
    { value: '', label: 'Seleccionar género' },
    { value: 'Masculino', label: 'Masculino' },
    { value: 'Femenino', label: 'Femenino' }
  ];

  const getEstadoCivilOptions = () => {
    const genero = watch('genero');
    if (genero === 'Masculino') {
      return [
        { value: '', label: 'Seleccionar estado civil' },
        { value: 'Soltero', label: 'Soltero' },
        { value: 'Casado', label: 'Casado' },
        { value: 'Unión Libre', label: 'Unión Libre' },
        { value: 'Viudo', label: 'Viudo' }
      ];
    } else if (genero === 'Femenino') {
      return [
        { value: '', label: 'Seleccionar estado civil' },
        { value: 'Soltera', label: 'Soltera' },
        { value: 'Casada', label: 'Casada' },
        { value: 'Unión Libre', label: 'Unión Libre' },
        { value: 'Viuda', label: 'Viuda' }
      ];
    }
    return [{ value: '', label: 'Primero selecciona tu género' }];
  };

  if (!isOpen) return null;

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full rounded-2xl p-8 text-center bg-white border border-stone-200/60 shadow-2xl">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-4xl">✓</span>
          </div>
          <h3 className="text-xs font-serif font-medium text-stone-800 mb-4">
            ¡Bienvenido a la Iglesia!
          </h3>
          <div className="mb-6">
            <p className="text-stone-600 mb-3">
              <strong>{submittedMemberData?.nombres} {submittedMemberData?.apellidos}</strong>, tu inscripción como miembro ha sido registrada exitosamente.
            </p>
            <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/60">
              <p className="text-xs text-stone-600 mb-2">
                📧 Hemos enviado un correo de confirmación a: <strong>{submittedMemberData?.email}</strong>
              </p>
              {submittedMemberData?.memberId && (
                <p className="text-xs text-stone-500">
                  ID de Miembro: {submittedMemberData.memberId}
                </p>
              )}
            </div>
          </div>
          <div className="border-t border-stone-200 pt-4">
            <p className="text-xs text-stone-600 mb-2">
              🙏 ¡Esperamos verte pronto en nuestros servicios!
            </p>
            <div className="text-xs text-stone-500">
              Cerrando automáticamente en unos segundos...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Clases reutilizables para inputs
  const inputBaseClass = "w-full px-4 py-3 border rounded-xl transition-all duration-300 focus:ring-4 focus:outline-none";
  const inputNormalClass = "border-stone-200/60 bg-white focus:border-purple-700 focus:ring-purple-500/20";
  const inputErrorClass = "border-red-500 focus:border-red-500 focus:ring-red-500/20";
  const getInputClass = (fieldError: any) => 
    `${inputBaseClass} ${fieldError ? inputErrorClass : inputNormalClass}`;

  // Clases para secciones tipo tarjeta
  const sectionCardClass = "bg-stone-50 rounded-2xl p-6 border border-stone-200/60 shadow-sm";
  const sectionTitleClass = "text-xs font-serif font-medium text-stone-800 mb-4 flex items-center gap-2";
  const labelClass = "block text-xs font-medium text-stone-700 mb-2";
  const errorTextClass = "mt-1 text-xs text-red-500";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full max-h-[85vh] flex flex-col rounded-2xl bg-white border border-stone-200/60 shadow-2xl">
        
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-stone-200 bg-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-stone-500 text-xs uppercase tracking-[0.2em] mb-1 font-medium">
                Formulario de registro
              </p>
              <h2 className="text-xs font-serif font-medium text-stone-800">
                Inscripción como Miembro
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-purple-600 to-red-500 mt-2 rounded-full"></div>
              <p className="text-stone-500 text-xs mt-2">
                Para: {eventTitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-stone-100 text-stone-500 hover:text-stone-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto bg-white">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          
          {/* Información del Documento */}
          <div className={sectionCardClass}>
            <h3 className={sectionTitleClass}>
              <span className="text-purple-700">📄</span>
              Información del Documento
            </h3>
            <div className="w-8 h-px bg-stone-300 mb-4"></div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Tipo de Documento *
                </label>
                <select
                  {...register('tipoDocumento')}
                  className={getInputClass(errors.tipoDocumento)}
                >
                  {tiposDocumento.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
                {errors.tipoDocumento && (
                  <p className={errorTextClass}>{errors.tipoDocumento.message}</p>
                )}
              </div>
              
              <div>
                <label className={labelClass}>
                  Número de Documento *
                </label>
                <input
                  type="text"
                  {...register('numeroDocumento')}
                  placeholder="123456789"
                  className={getInputClass(errors.numeroDocumento)}
                />
                {errors.numeroDocumento && (
                  <p className={errorTextClass}>{errors.numeroDocumento.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Información Personal */}
          <div className={sectionCardClass}>
            <h3 className={sectionTitleClass}>
              <span className="text-purple-700">👤</span>
              Información Personal
            </h3>
            <div className="w-8 h-px bg-stone-300 mb-4"></div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelClass}>
                  Nombres *
                </label>
                <input
                  type="text"
                  {...register('nombres')}
                  placeholder="Juan Carlos"
                  className={getInputClass(errors.nombres)}
                />
                {errors.nombres && (
                  <p className={errorTextClass}>{errors.nombres.message}</p>
                )}
              </div>
              
              <div>
                <label className={labelClass}>
                  Apellidos *
                </label>
                <input
                  type="text"
                  {...register('apellidos')}
                  placeholder="Pérez González"
                  className={getInputClass(errors.apellidos)}
                />
                {errors.apellidos && (
                  <p className={errorTextClass}>{errors.apellidos.message}</p>
                )}
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelClass}>
                  Género *
                </label>
                <select
                  {...register('genero')}
                  className={getInputClass(errors.genero)}
                >
                  {generos.map(genero => (
                    <option key={genero.value} value={genero.value}>{genero.label}</option>
                  ))}
                </select>
                {errors.genero && (
                  <p className={errorTextClass}>{errors.genero.message}</p>
                )}
              </div>
              
              <div>
                <label className={labelClass}>
                  Estado Civil *
                </label>
                <select
                  {...register('estadoCivil')}
                  disabled={!watch('genero')}
                  className={`${getInputClass(errors.estadoCivil)} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {getEstadoCivilOptions().map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {errors.estadoCivil && (
                  <p className={errorTextClass}>{errors.estadoCivil.message}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Fecha de Nacimiento *
                </label>
                <input
                  type="date"
                  {...register('fechaNacimiento')}
                  className={getInputClass(errors.fechaNacimiento)}
                />
                {errors.fechaNacimiento && (
                  <p className={errorTextClass}>{errors.fechaNacimiento.message}</p>
                )}
              </div>
              
              <div>
                <label className={labelClass}>
                  Zona o Barrio *
                </label>
                <input
                  type="text"
                  {...register('zonaBarrio')}
                  placeholder="Ej: Centro, La Chinita, Obrero"
                  className={getInputClass(errors.zonaBarrio)}
                />
                {errors.zonaBarrio && (
                  <p className={errorTextClass}>{errors.zonaBarrio.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Información de Contacto */}
          <div className={sectionCardClass}>
            <h3 className={sectionTitleClass}>
              <span className="text-purple-700">📞</span>
              Información de Contacto
            </h3>
            <div className="w-8 h-px bg-stone-300 mb-4"></div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Email <span className="text-stone-400">(opcional)</span>
                </label>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="juan@ejemplo.com"
                  className={getInputClass(errors.email)}
                />
                {errors.email && (
                  <p className={errorTextClass}>{errors.email.message}</p>
                )}
              </div>
              
              <div>
                <label className={labelClass}>
                  Teléfono *
                </label>
                <input
                  type="tel"
                  {...register('telefono')}
                  placeholder="3001234567"
                  className={getInputClass(errors.telefono)}
                />
                {errors.telefono && (
                  <p className={errorTextClass}>{errors.telefono.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Consentimientos y Autorizaciones */}
          <div className={sectionCardClass}>
            <h3 className={sectionTitleClass}>
              <span className="text-purple-700">📋</span>
              Consentimientos y Autorizaciones
            </h3>
            <div className="w-8 h-px bg-stone-300 mb-4"></div>
            
            <div className="space-y-4">
              {/* Consentimiento de Datos */}
              <div className="bg-white rounded-xl p-4 border border-stone-200/60">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('consentimientoDatos')}
                    className={`mt-1 w-5 h-5 rounded border-2 transition-colors ${
                      errors.consentimientoDatos
                        ? 'border-red-500'
                        : 'border-stone-300'
                    } text-purple-700 focus:ring-purple-500 focus:ring-2`}
                  />
                  <div className="flex-1">
                    <label className="text-xs text-stone-700">
                      <span className="font-medium">He leído la Política de Tratamiento de Datos Personales y autorizo el tratamiento de mis datos.</span>
                      <span className="block mt-1 text-xs text-stone-500">
                        Autorizo a la Iglesia Cristiana Cuadrangular Central de Apartadó a recopilar, almacenar y procesar mis datos personales 
                        conforme a la Ley 1581 de 2012 para fines religiosos, pastorales y comunitarios.{' '}
                        <button
                          type="button"
                          onClick={() => setShowDataModal(true)}
                          className="text-purple-700 hover:text-purple-600 underline font-medium transition-colors"
                        >
                          Ver política completa
                        </button>
                      </span>
                    </label>
                    {errors.consentimientoDatos && (
                      <p className={errorTextClass}>{errors.consentimientoDatos.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Autorización de Imagen */}
              <div className="bg-white rounded-xl p-4 border border-stone-200/60">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('autorizacionImagen')}
                    className={`mt-1 w-5 h-5 rounded border-2 transition-colors ${
                      errors.autorizacionImagen
                        ? 'border-red-500'
                        : 'border-stone-300'
                    } text-purple-700 focus:ring-purple-500 focus:ring-2`}
                  />
                  <div className="flex-1">
                    <label className="text-xs text-stone-700">
                      <span className="font-medium">Autorizo a la Iglesia para captar, almacenar, reproducir y publicar mi imagen en fotografías y/o videos.</span>
                      <span className="block mt-1 text-xs text-stone-500">
                        Autorizo el uso de mi imagen en medios impresos, página web oficial, transmisiones y redes sociales institucionales, 
                        con fines religiosos, informativos y comunitarios de la Iglesia Cristiana Cuadrangular Central de Apartadó.
                      </span>
                    </label>
                    {errors.autorizacionImagen && (
                      <p className={errorTextClass}>{errors.autorizacionImagen.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="p-4 rounded-xl border-l-4 border-red-500 bg-red-50">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-500 text-xs">⚠️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-xs font-medium text-red-800">
                    Error al registrar miembro
                  </h3>
                  <p className="mt-1 text-xs text-red-700">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 border-2 border-stone-300 text-stone-700 hover:bg-stone-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !consentimientoDatos || !autorizacionImagen}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 text-white transform hover:scale-105 shadow-lg ${
                isLoading || !consentimientoDatos || !autorizacionImagen
                  ? 'bg-stone-400 cursor-not-allowed'
                  : 'bg-purple-800 hover:bg-purple-700 hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Enviando...</span>
                </div>
              ) : (
                'Completar Inscripción'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
      
      {/* Modal de Bienvenida */}
      {showWelcomeModal && submittedMemberData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl transform transition-all duration-300 bg-white border border-stone-200/60">
            
            {/* Icono y título */}
            <div className="text-center mb-6">
              <div className="mx-auto w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mb-4 shadow-lg">
                <img 
                  src="/src/assets/images/Logo_Inglesia_Cuadrangular.png" 
                  alt="Logo ICC" 
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h2 className="text-xs font-serif font-medium text-stone-900 mb-2">
                {submittedMemberData?.genero === 'Femenino' 
                  ? '¡Bienvenida a nuestra comunidad!' 
                  : '¡Bienvenido a nuestra comunidad!'}
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-purple-600 to-red-500 mx-auto mb-3 rounded-full"></div>
              <p className="text-xs text-stone-600">
                Tu inscripción como miembro ha sido completada exitosamente
              </p>
            </div>

            {/* Información del miembro */}
            <div className="rounded-xl p-4 mb-6 bg-stone-50 border border-stone-200/60">
              <h3 className="font-medium text-stone-700 mb-3">
                Información de registro:
              </h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-stone-500">
                    Nombre:
                  </span>
                  <span className="font-medium text-stone-700">
                    {submittedMemberData.nombreCompleto}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">
                    Documento:
                  </span>
                  <span className="font-medium text-stone-700">
                    {submittedMemberData.documentoCompleto}
                  </span>
                </div>
                {submittedMemberData.email && (
                  <div className="flex justify-between">
                    <span className="text-stone-500">
                      Email:
                    </span>
                    <span className="font-medium text-stone-700">
                      {submittedMemberData.email}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-stone-500">
                    ID de miembro:
                  </span>
                  <span className="font-medium text-stone-700">
                    {submittedMemberData.memberId}
                  </span>
                </div>
              </div>
            </div>

            {/* Mensaje informativo */}
            <div className="rounded-xl p-4 mb-6 border-l-4 border-purple-700 bg-purple-50">
              <div className="flex">
                <span className="text-purple-700 text-xs mr-3">📧</span>
                <div>
                  {submittedMemberData.email ? (
                    <>
                      <p className="text-xs text-purple-800">
                        <strong>¡Revisa tu email!</strong>
                      </p>
                      <p className="text-xs mt-1 text-purple-600">
                        Te hemos enviado un certificado de membresía y toda la información importante sobre nuestra comunidad.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-purple-800">
                        <strong>¡Registro completado!</strong>
                      </p>
                      <p className="text-xs mt-1 text-purple-600">
                        Guarda tu número de documento, lo necesitarás para iniciar sesión en la plataforma.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowWelcomeModal(false);
                  setSubmittedMemberData(null);
                  onClose();
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium transition-colors bg-stone-100 text-stone-700 hover:bg-stone-200"
              >
                Cerrar
              </button>
              {submittedMemberData.email && (
                <button
                  onClick={() => {
                    window.open(`mailto:${submittedMemberData.email}`, '_blank');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg font-medium bg-purple-800 text-white hover:bg-purple-700 transition-all transform hover:scale-105"
                >
                  Abrir Email
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Tratamiento de Datos */}
      <DataTreatmentModal
        isOpen={showDataModal}
        onClose={() => setShowDataModal(false)}
      />
    </div>
  );
};

export default MembershipForm;
